import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    // v79.186 — Founder-reported hang on login + edit-post. Axios's
    // default is NO timeout, so any single slow request (link-preview
    // scraper on a dead FB URL, GDELT 429 retry, a stuck WebSocket
    // upgrade) freezes the calling component indefinitely and the
    // whole page feels unresponsive. Cap every request at 15 s so a
    // slow endpoint surfaces as a plain error instead of an infinite
    // spinner. GETs still enjoy the 429-retry ladder below.
    timeout: 15000,
});

// Attach bearer token if present
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("aduku_token");
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

/**
 * v78.13 — Global 429 retry with exponential backoff.
 *
 * Browse infinite-scroll, Home periodic refresh, and parallel widget loads
 * can momentarily breach the backend rate limiter (especially when a user
 * pull-to-refreshes mid-scroll). Without this, axios bubbles the 429 up
 * to the calling component where it surfaces as a React error overlay.
 *
 * Retries up to 2 times with 1s → 2s delay, respecting the `Retry-After`
 * header when the server sends one. Only retries GETs to avoid double-
 * submitting writes.
 */
api.interceptors.response.use(
    (r) => r,
    async (err) => {
        const cfg = err?.config;
        const status = err?.response?.status;
        // v79.224 — Global 401 recovery. If the backend rejects the
        // Bearer token (expired, rotated JWT_SECRET, deleted user) the
        // frontend was keeping the stale state and surfacing the raw
        // "Invalid token" detail to users. Instead, clear the local
        // session so the next action triggers the sign-in gate, and
        // return a friendly error to the caller. We skip the login /
        // auth-me endpoints themselves — their callers already handle
        // 401 inline.
        if (status === 401 && cfg && !cfg._auth401Handled) {
            const url = (cfg.url || "").toString();
            const isAuthProbe = url.startsWith("/auth/me")
                || url.startsWith("/auth/login")
                || url.startsWith("/auth/register")
                || url.startsWith("/auth/google")
                || url.startsWith("/auth/logout");
            if (!isAuthProbe) {
                cfg._auth401Handled = true;
                try { localStorage.removeItem("aduku_token"); } catch { /* ignore */ }
                try {
                    window.dispatchEvent(new CustomEvent("aduku:user-updated", { detail: null }));
                    window.dispatchEvent(new CustomEvent("aduku:signin-required"));
                } catch { /* ignore */ }
                // Replace the backend detail with a user-friendly one
                // so toasts stop surfacing raw "Invalid token" strings.
                if (err?.response?.data) {
                    err.response.data.detail = "Your session has expired. Please sign in again.";
                }
            }
        }
        if (!cfg || status !== 429 || (cfg.method || "get").toLowerCase() !== "get") throw err;
        cfg._retry429 = (cfg._retry429 || 0) + 1;
        if (cfg._retry429 > 2) throw err;
        const hintHeader = err?.response?.headers?.["retry-after"];
        const hintMs = hintHeader ? Math.min(parseInt(hintHeader, 10) * 1000, 5000) : 0;
        const delay = hintMs || (cfg._retry429 === 1 ? 1000 : 2000);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(cfg);
    }
);

/**
 * v78.13 — Coalesce concurrent identical GETs.
 *
 * If two scroll listeners or a re-render fires the same `/listings?...`
 * fetch in the same tick, share the in-flight axios promise so we only
 * hit the network once. This complements the 429 retry above by reducing
 * the number of duplicate requests we send in the first place.
 *
 * Only applied to the bare `api.get(url)` form (no per-call config) since
 * coalescing across different headers/data would be unsafe.
 */
const _inflightGet = new Map();
const _origGet = api.get.bind(api);
api.get = function coalescedGet(url, config) {
    if (config !== undefined) return _origGet(url, config);
    const pending = _inflightGet.get(url);
    if (pending) return pending;
    const p = _origGet(url).finally(() => { _inflightGet.delete(url); });
    _inflightGet.set(url, p);
    return p;
};

/**
 * Cached GET — used for static-ish endpoints (categories, countries, stats)
 * that several components hit on every page load.
 *
 * - In-flight promises are de-duplicated so React StrictMode's double-effect
 *   never fires the same network request twice.
 * - Responses live in memory for `ttlMs` (default 60 s) so within a session
 *   the second component to need the data gets it instantly.
 * - Pass `{ force: true }` to bypass the cache (e.g. pull-to-refresh).
 */
const _cache = new Map();
const _inflight = new Map();
const _DEFAULT_TTL = 60 * 1000;
export function cachedGet(path, { ttlMs = _DEFAULT_TTL, force = false } = {}) {
    const now = Date.now();
    if (!force) {
        const cached = _cache.get(path);
        if (cached && cached.expires > now) return Promise.resolve(cached.value);
        const pending = _inflight.get(path);
        if (pending) return pending;
    }
    const p = api.get(path)
        .then((r) => {
            _cache.set(path, { value: r, expires: Date.now() + ttlMs });
            _inflight.delete(path);
            return r;
        })
        .catch((err) => {
            _inflight.delete(path);
            throw err;
        });
    _inflight.set(path, p);
    return p;
}

/**
 * Invalidate the in-memory cachedGet store. Pass a prefix to only purge
 * keys starting with that string (e.g. "/home/feed" wipes every
 * country-scoped variant). With no argument, clears the entire cache.
 *
 * Call this from `PostAd` / `delete listing` / any mutation path so the
 * next /home or /browse fetch goes to the network and the user
 * immediately sees their freshly-posted (or removed) listing reflected.
 */
export function invalidateCache(prefix) {
    if (!prefix) { _cache.clear(); _inflight.clear(); return; }
    for (const k of Array.from(_cache.keys())) {
        if (k.startsWith(prefix)) _cache.delete(k);
    }
    for (const k of Array.from(_inflight.keys())) {
        if (k.startsWith(prefix)) _inflight.delete(k);
    }
}

export function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || "Something went wrong";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
